import { Team, CompactBoxScore, WeatherType, Player, PlayerStats, InjurySeverity } from '../types/game';
import { randomInt, clamp } from './math/variance';
import { getPositionGroup } from './matchEngine';
import { addPlayerStats } from './playerStats';
import { disciplineTurnoverPoints } from './programMeters';
import { fatigueInjuryMultiplier } from './training';
import { simulateTeamPenalties } from './penalties';

// Calibration constants (tuned against src/sim/tests/macroSim.test.ts targets)
const HOME_FIELD_RATING = 1;
const DRIVE_DELTA_SCALE = { td: 2.6, fg: 0.7, turnover: 0.8 };
const BASE_DRIVE_ODDS = { td: 25, fg: 12, turnover: 15 };
// A drive that stalls ends in a missed field goal this often (about 3 kicks in 4 are good)
const MISSED_FG_SHARE = 0.1;

/** The game's kicks, on the team's starting kicker (light teams carry no kicker: nothing to credit). */
function creditKicks(team: Team, made: number, attempted: number) {
  const kicker = team.roster.find((p) => p.position === 'K' && p.depthChartTier === 1) ?? team.roster.find((p) => p.position === 'K');
  if (!kicker || attempted === 0) return;
  kicker.stats.fieldGoalsMade += made;
  kicker.stats.fieldGoalsAttempted += attempted;
}
// A penalty yard is worth about 0.07 points; a rating point about 4.5 points of margin
const PENALTY_YARD_RATING = 0.016;

/** One Kansas Plan overtime possession from the opponent's 10: points scored (7, 3 or 0). */
function resolveOvertimePossession(delta: number): number {
  const roll = Math.random() * 100;
  const tdChance = clamp(45 + delta * 1.6, 20, 70);
  const fgChance = 30;
  if (roll < tdChance) return 7;
  if (roll < tdChance + fgChance) return 3;
  return 0;
}

/** Splits `total` across players by weight (whole numbers, remainder to the first). */
function splitByWeight(total: number, shares: [Player | undefined, number][], stat: keyof PlayerStats) {
  const available = shares.filter((s): s is [Player, number] => s[0] !== undefined);
  const weightSum = available.reduce((sum, [, w]) => sum + w, 0) || 1;
  let assigned = 0;
  available.forEach(([player, weight], i) => {
    const amount = i === available.length - 1 ? total - assigned : Math.round((total * weight) / weightSum);
    assigned += amount;
    addPlayerStats(player.stats, { [stat]: amount });
  });
}

/** Picks one player by weight. */
function pickByWeight(shares: [Player | undefined, number][]): Player | undefined {
  const available = shares.filter((s): s is [Player, number] => s[0] !== undefined);
  let roll = Math.random() * available.reduce((sum, [, w]) => sum + w, 0);
  for (const [player, weight] of available) {
    roll -= weight;
    if (roll <= 0) return player;
  }
  return available[0]?.[0];
}

/**
 * Credits individual season stats for an AI vs. AI game using the depth-chart
 * usage shares from design spec 11.1.
 */
function distributeMacroStats(
  team: Team,
  totals: { passYds: number; rushYds: number; touchdowns: number; interceptions: number; fumbles: number; opponentInterceptions: number; opponentPlays: number }
) {
  const qb = getPositionGroup(team, 'QB');
  const rb = getPositionGroup(team, 'RB');
  const wr = getPositionGroup(team, 'WR');
  const te = getPositionGroup(team, 'TE');
  const receivers: [Player | undefined, number][] = [[wr[0], 38], [wr[1], 24], [wr[2], 15], [te[0], 12], [rb[0], 11]];
  const rushers: [Player | undefined, number][] = [[rb[0], 65], [rb[1], 25], [qb[0], 10]];
  const passers: [Player | undefined, number][] = [[qb[0], 85], [qb[1], 15]];

  const completions = Math.round(totals.passYds / 11);
  splitByWeight(totals.passYds, passers, 'passYards');
  splitByWeight(completions, passers, 'passCompletions');
  splitByWeight(Math.round(completions / 0.53), passers, 'passAttempts');
  splitByWeight(totals.passYds, receivers, 'receivingYards');
  splitByWeight(completions, receivers, 'receptions');
  splitByWeight(totals.rushYds, rushers, 'rushYards');
  splitByWeight(Math.round(totals.rushYds / 4.5), rushers, 'rushAttempts');

  for (let i = 0; i < totals.touchdowns; i++) {
    if (Math.random() < 0.55) {
      const passer = pickByWeight(passers);
      const target = pickByWeight(receivers);
      if (passer) addPlayerStats(passer.stats, { passTDs: 1 });
      if (target) addPlayerStats(target.stats, { receivingTDs: 1 });
    } else {
      const rusher = pickByWeight(rushers);
      if (rusher) addPlayerStats(rusher.stats, { rushTDs: 1 });
    }
  }
  if (qb[0]) addPlayerStats(qb[0].stats, { interceptionsThrown: totals.interceptions });
  if (rb[0]) addPlayerStats(rb[0].stats, { fumblesLost: totals.fumbles });

  // Defense: tackles from the opponent's offensive snaps, sacks, and interceptions caught
  const lb = getPositionGroup(team, 'LB');
  const cb = getPositionGroup(team, 'CB');
  const de = getPositionGroup(team, 'DE');
  const dt = getPositionGroup(team, 'DT');
  const s = getPositionGroup(team, 'S');
  const tacklers: [Player | undefined, number][] = [[lb[0], 22], [lb[1], 16], [de[0], 12], [dt[0], 10], [s[0], 14], [cb[0], 12], [cb[1], 8], [de[1], 6]];
  splitByWeight(Math.round(totals.opponentPlays * 0.85), tacklers, 'tackles');
  splitByWeight(randomInt(0, 4), [[de[0], 55], [dt[0], 25], [lb[0], 20]], 'sacks');
  splitByWeight(randomInt(2, 7), [[lb[0], 35], [de[0], 30], [dt[0], 20], [lb[1], 15]], 'tacklesForLoss');
  for (let i = 0; i < totals.opponentInterceptions; i++) {
    const defender = pickByWeight([[cb[0], 55], [s[0], 35], [lb[0], 10]]);
    if (defender) addPlayerStats(defender.stats, { interceptionsCaught: 1 });
  }

  gameDayLineup(team).forEach((p) => p && (p.stats.gamesPlayed += 1));
}

/** Can play this week: academically eligible and healthy (same rule as the live engine). */
export const isAvailable = (p: Player) => p.academics.isEligible && p.condition.injuryStatus === 'HEALTHY';

// A first-string slot nobody can fill is played by a walk-on
const EMPTY_SLOT_RATING = 40;

/**
 * The team that actually takes the field: each first-stringer who is injured or ineligible is replaced
 * by the next available player at his position (2nd string first, then the best rated).
 */
export function gameDayLineup(team: Team): (Player | undefined)[] {
  const used = new Set<string>();
  const starters = team.roster.filter((p) => p.depthChartTier === 1);
  return starters.map((starter) => {
    if (isAvailable(starter)) return starter;
    const sub = team.roster
      .filter((p) => p.depthChartTier !== 1 && !used.has(p.id) && isAvailable(p) && (p.position === starter.position || p.secondaryPosition === starter.position))
      .sort((a, b) => a.depthChartTier - b.depthChartTier || b.overallRating - a.overallRating)[0];
    if (sub) used.add(sub.id);
    return sub;
  });
}

// Per-game injury odds for each player in the lineup (about one minor injury per team per game)
const GAME_INJURY_ODDS = { seasonEnding: 0.0015, moderate: 0.01, dinged: 0.03 };

/**
 * Injuries from one game (live or simulated) for everyone who played. Tired players (high fatigue)
 * get hurt more; kickers rarely. Returns who was hurt and how badly.
 */
export function rollGameInjuries(team: Team, week: number): { player: Player; severity: InjurySeverity }[] {
  const hurt: { player: Player; severity: InjurySeverity }[] = [];
  gameDayLineup(team).forEach((p) => {
    if (!p || p.condition.injuryStatus !== 'HEALTHY') return;
    const risk = fatigueInjuryMultiplier(p.condition.seasonWear) * (p.position === 'K' || p.position === 'P' ? 0.3 : 1) * (1 - (team.injuryResistance ?? 0));
    const roll = Math.random();
    let severity: InjurySeverity = 'HEALTHY';
    let weeks = 0;
    if (roll < GAME_INJURY_ODDS.seasonEnding * risk) [severity, weeks] = ['SEASON_ENDING', 20];
    else if (roll < GAME_INJURY_ODDS.moderate * risk) [severity, weeks] = ['MODERATE', randomInt(2, 4)];
    else if (roll < GAME_INJURY_ODDS.dinged * risk) [severity, weeks] = ['DINGED', 1];
    if (severity === 'HEALTHY') return;
    p.condition.injuryStatus = severity;
    p.condition.injuryWeeksRemaining = weeks;
    p.condition.injuredInWeek = week;
    hurt.push({ player: p, severity });
  });
  return hurt;
}

/** Average overall rating of the game-day lineup (first string, with backups in for missing starters). */
export function teamStarterRating(team: Team): number {
  const edge = (team.gameDayEdge ?? 0) + (team.fridayEdge ?? 0); // the coach's paid staff, and this week's dilemma edge
  if (team.lightRating !== undefined) return team.lightRating + edge; // another state's light team
  const lineup = gameDayLineup(team);
  return lineup.reduce((sum, p) => sum + (p?.overallRating ?? EMPTY_SLOT_RATING), 0) / (lineup.length || 1) + edge;
}

/**
 * Fast sub-millisecond AI vs AI background macro match simulation.
 * Credits individual player stats; team records are applied by the caller
 * (see applyGameResult in scheduleEngine) so playoff games stay off the regular-season record.
 */
export function simulateMacroMatch(
  gameId: string,
  week: number,
  homeTeam: Team,
  awayTeam: Team,
  weather: WeatherType = 'CLEAR'
): CompactBoxScore {
  const homeOvr = teamStarterRating(homeTeam);
  const awayOvr = teamStarterRating(awayTeam);
  // Flags: each side's penalty yards push the game toward the other
  const homeFlags = simulateTeamPenalties(homeTeam, false);
  const awayFlags = simulateTeamPenalties(awayTeam, true);
  const delta = (homeOvr + HOME_FIELD_RATING) - awayOvr + (awayFlags.yards - homeFlags.yards) * PENALTY_YARD_RATING;

  // Undisciplined teams turn it over more
  const homeSloppiness = disciplineTurnoverPoints(homeTeam);
  const awaySloppiness = disciplineTurnoverPoints(awayTeam);

  const totalDrives = 11 + randomInt(-1, 2);
  let homeScore = 0;
  let awayScore = 0;
  let homeTDs = 0;
  let awayTDs = 0;
  const kicks = { home: { made: 0, missed: 0 }, away: { made: 0, missed: 0 } };

  let homePassYds = 0;
  let homeRushYds = 0;
  let homeTO = 0;

  let awayPassYds = 0;
  let awayRushYds = 0;
  let awayTO = 0;

  for (let i = 0; i < totalDrives; i++) {
    // Home drive
    const homeRoll = Math.random() * 100;
    const homeTdProb = clamp(BASE_DRIVE_ODDS.td + delta * DRIVE_DELTA_SCALE.td, 5, 45);
    const homeFgProb = clamp(BASE_DRIVE_ODDS.fg + delta * DRIVE_DELTA_SCALE.fg, 3, 25);
    const homeToProb = clamp(BASE_DRIVE_ODDS.turnover - delta * DRIVE_DELTA_SCALE.turnover + (weather === 'HEAVY_RAIN' ? 5 : 0) + homeSloppiness, 4, 24);

    if (homeRoll < homeTdProb) {
      homeScore += 7;
      homeTDs += 1;
      homePassYds += randomInt(20, 45);
      homeRushYds += randomInt(15, 35);
    } else if (homeRoll < homeTdProb + homeFgProb) {
      homeScore += 3;
      kicks.home.made += 1;
      homePassYds += randomInt(15, 30);
      homeRushYds += randomInt(10, 25);
    } else if (homeRoll < homeTdProb + homeFgProb + homeToProb) {
      homeTO += 1;
      homePassYds += randomInt(5, 15);
      homeRushYds += randomInt(0, 10);
    } else {
      homePassYds += randomInt(3, 10);
      homeRushYds += randomInt(3, 8);
      if (Math.random() < MISSED_FG_SHARE) kicks.home.missed += 1;
    }

    // Away drive
    const awayRoll = Math.random() * 100;
    const awayTdProb = clamp(BASE_DRIVE_ODDS.td - delta * DRIVE_DELTA_SCALE.td, 5, 45);
    const awayFgProb = clamp(BASE_DRIVE_ODDS.fg - delta * DRIVE_DELTA_SCALE.fg, 3, 25);
    const awayToProb = clamp(BASE_DRIVE_ODDS.turnover + delta * DRIVE_DELTA_SCALE.turnover + (weather === 'HEAVY_RAIN' ? 5 : 0) + awaySloppiness, 4, 24);

    if (awayRoll < awayTdProb) {
      awayScore += 7;
      awayTDs += 1;
      awayPassYds += randomInt(20, 45);
      awayRushYds += randomInt(15, 35);
    } else if (awayRoll < awayTdProb + awayFgProb) {
      awayScore += 3;
      kicks.away.made += 1;
      awayPassYds += randomInt(15, 30);
      awayRushYds += randomInt(10, 25);
    } else if (awayRoll < awayTdProb + awayFgProb + awayToProb) {
      awayTO += 1;
      awayPassYds += randomInt(5, 15);
      awayRushYds += randomInt(0, 10);
    } else {
      awayPassYds += randomInt(3, 10);
      awayRushYds += randomInt(3, 8);
      if (Math.random() < MISSED_FG_SHARE) kicks.away.missed += 1;
    }
  }

  // Kansas Plan overtime: alternate possessions from the 10 until the tie is broken
  let overtimePeriods = 0;
  while (homeScore === awayScore) {
    overtimePeriods += 1;
    const homeOT = resolveOvertimePossession(delta);
    const awayOT = resolveOvertimePossession(-delta);
    homeScore += homeOT;
    awayScore += awayOT;
    if (homeOT === 7) homeTDs += 1;
    if (awayOT === 7) awayTDs += 1;
    if (homeOT === 3) kicks.home.made += 1;
    if (awayOT === 3) kicks.away.made += 1;
  }

  // Individual stats (turnovers split ~60% interceptions / 40% fumbles)
  const homeINT = Math.round(homeTO * 0.6);
  const awayINT = Math.round(awayTO * 0.6);
  const plays = (passYds: number, rushYds: number) => Math.round(passYds / 11 / 0.53 + rushYds / 4.5);
  distributeMacroStats(homeTeam, {
    passYds: homePassYds, rushYds: homeRushYds, touchdowns: homeTDs, interceptions: homeINT, fumbles: homeTO - homeINT,
    opponentInterceptions: awayINT, opponentPlays: plays(awayPassYds, awayRushYds)
  });
  distributeMacroStats(awayTeam, {
    passYds: awayPassYds, rushYds: awayRushYds, touchdowns: awayTDs, interceptions: awayINT, fumbles: awayTO - awayINT,
    opponentInterceptions: homeINT, opponentPlays: plays(homePassYds, homeRushYds)
  });

  creditKicks(homeTeam, kicks.home.made, kicks.home.made + kicks.home.missed);
  creditKicks(awayTeam, kicks.away.made, kicks.away.made + kicks.away.missed);

  const homeQb = homeTeam.roster.find((p) => p.position === 'QB') || homeTeam.roster[0];
  const homeRb = homeTeam.roster.find((p) => p.position === 'RB') || homeTeam.roster[0];

  return {
    gameId,
    week,
    homeTeamId: homeTeam.id,
    awayTeamId: awayTeam.id,
    homeScore,
    awayScore,
    weather,
    ...(overtimePeriods > 0 && { overtimePeriods }),
    leaders: {
      passing: {
        playerId: homeQb.id,
        name: `${homeQb.firstName} ${homeQb.lastName}`,
        line: `${Math.floor(homePassYds / 12)}/${Math.floor(homePassYds / 7)}, ${homePassYds} YDS, ${Math.floor(homeScore / 7)} TD`
      },
      rushing: {
        playerId: homeRb.id,
        name: `${homeRb.firstName} ${homeRb.lastName}`,
        line: `${Math.floor(homeRushYds / 4.5)} CAR, ${homeRushYds} YDS`
      },
      receiving: {
        playerId: homeTeam.roster[2]?.id || 'rec_1',
        name: `${homeTeam.roster[2]?.lastName || 'Receiver'}`,
        line: `5 REC, ${Math.floor(homePassYds * 0.45)} YDS`
      },
      defense: {
        playerId: homeTeam.roster[5]?.id || 'def_1',
        name: `${homeTeam.roster[5]?.lastName || 'Defender'}`,
        line: `8 TKL, 2 TFL`
      }
    },
    teamTotals: {
      homeTotalYards: homePassYds + homeRushYds,
      homePassYards: homePassYds,
      homeRushYards: homeRushYds,
      homeTurnovers: homeTO,
      awayTotalYards: awayPassYds + awayRushYds,
      awayPassYards: awayPassYds,
      awayRushYards: awayRushYds,
      awayTurnovers: awayTO,
      homePenalties: homeFlags.count,
      homePenaltyYards: homeFlags.yards,
      awayPenalties: awayFlags.count,
      awayPenaltyYards: awayFlags.yards
    }
  };
}
